import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            cacheTime: 1000 * 60 * 5, // 5 minutes // default is 5 minutes
            staleTime: 0, // always keep fresh data // default is 0
            retry: 3, // 3 times before sending error
            retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // 2 seconds, 4 seconds, 8 seconds
        },
    },
});

export enum QueryKey {
    SITES = 'sites',
    SITE_RAPS = 'siteRaps',
    PROGRAM_TYPES = 'programTypes',
    ACCESS_PARTNERS = 'accessPartners',
    ALERT_TRACKS = 'alertTracks',
    ALERT_TRACKING_USERS = 'alertTrackingUsers',
    ALERT_TRACKING_PROGRAMS = 'alertTrackingPrograms',
    AUDIT_TRAIL = 'auditTrail',
    AUDIT_TRAIL_RECENT = 'auditTrailRecent',
    COMPANIES = 'companies',
    COMPANY_BRANDING = 'companyBranding',
    PROGRAMS = 'programs',
    PROGRAM_BY_COMPANY = 'programsByCompany',
    PROGRAM_OPERATORS = 'programOperators',
    USERS = 'users',
    IDENTIFY_MARKET_ZONE = 'identifyMarketZone',
    MARKET_ZONES = 'marketZones',
    ASYNC_JOB = 'asyncJob',
    SITE_LABELS = 'siteLabels',
    SITE_LOCATIONS = 'siteLocations',
    SITE_SAN_INFO = 'siteSanInfo',
    SITE_UTILITIES = 'siteUtilities',
    SITE_MARKETS = 'siteMarkets',
    IMPACT = 'impact',
    UNKNOWN_VEN_ERRORS = 'unknownVenErrors',
    VEN_OVERVIEW = 'venOverview',
    CUSTOMER_VENS = 'customerVens',
    CUSTOMER_VEN_LOGS = 'customerVenLogs',
    CUSTOMER_VENS_CONTROL_PROVIDERS = 'customerVensControlProviders',
    CLOUD_VENS = 'cloudVens',
    CLOUD_VEN_LOGS = 'cloudVenLogs',
    CLOUD_VEN_CERTIFICATE = 'cloudVenCertificate',
    PRICE_RESPONSE_TRIGGERS = 'priceResponseTriggers',
    CLEAN_RESPONSE_TRIGGERS = 'clearResponseTriggers',
    PRICE_RESPONSE_REPORTING = 'priceResponseReporting',
    DRRS_LOCATIONS = 'drrsLocations',
    DRRS_LOCATION_SANS = 'drrsLocationSans',
    DRRS_LOCATION_STATUSES = 'drrsLocationStatuses',
    DRRS_LOCATION_LSES = 'drrsLocationLses',
    DRRS_LOCATION_UDCS = 'drrsLocationUdcs',
    DRRS_LOCATION_SUBLAPS = 'drrsLocationSublaps',
    DRRS_LOCATION_RESOURCE_NAMES = 'drrsLocationResourceNames',
    DRRS_LOCATION_NAMES = 'name',
    DRRS_LOCATION_RESOURCE_IDS = 'drrsLocationLocationIds',
    DRRS_REGISTRATIONS = 'drrsRegistrations',
    DRRS_REGISTRATION_STATUSES = 'drrsRegistrationStatuses',
    DRRS_REGISTRATION_UDCS = 'drrsRegistrationUdcs',
    DRRS_REGISTRATION_SUBLAPS = 'drrsRegistrationSublaps',
    DRRS_REGISTRATION_RESOURCE_IDS = 'drrsRegistrationResourceIds',
    DRRS_REGISTRATION_LOCATIONS = 'drrsRegistrationLocations',
    DRRS_SYNCS = 'drrsSyncs',
    CMRI_MARKET_AWARDS = 'cmriMarketAwards',
    UTILITIES = 'utilities',
    GREENBUTTONS = 'greenbuttons',
    CARBON_INTENSITY = 'carbonIntensity',
    REAL_TIME_PRICES = 'realTimePrices',
    MARKET_PRICES = 'marketPrices',
    SETTLEMENTS = 'settlements',
    VENDOR_BILL_ITEMS = 'vendor-bill-items',
    VENDOR_BILL_ITEM_PROGRAMS = 'vendor-bill-item-programs',
    VENDOR_BILL_ITEMS_UTILITIES = 'vendor-bill-item-utilities',
    VENDOR_BILL_TRANSACTION_IDS = 'vendor-bill-transaction_ids',
    CUSTOMER_BILL_REPORTS = 'customer-bill-reports',
    CUSTOMER_BILL_UTILITIES = 'customer-bill-utilities',
    CUSTOMER_BILL_DETAIL = 'customer-bill-detail',
    CUSTOMER_BILL_FILES = 'customer-bill-files',
    CUSTOMER_BILL_FILE_SOURCES = 'customer-bill-file-sources',
    CUSTOMER_BILL_FILE_CUSTOMERS = 'customer-bill-file-customers',
    CUSTOMER_BILL_FILE_DETAIL = 'customer-bill-file-detail',
    CUSTOMER_BILL_FILE_PDF_PREVIEW = 'customer-bill-file-pdf-preview',
    STANDBY_PERIODS = 'standby-periods',
    EVENTS = 'events',
    EVENTS_METRICS = 'eventsMetrics',
    EVENTS_COMPANY_PARTICIPATION = 'eventsCompanyParticipation',
    EVENTS_COUNT = 'eventsCount',
    EVENT_ACCOUNT_MANAGERS = 'eventAccountManagers',
    ACCOUNT_MANAGERS = 'accountManagers',
    RESOURCES = 'resources',
    INTERVAL_DATA_REPORTING = 'intervalDataReporting',
    ENERGY_USAGES = 'energyUsages',
    ENERGY_USAGES_EVENTS = 'energyUsageEvents',
    ENERGY_USAGES_REQUESTS = 'energyUsagesRequests',
    WEATHER_FORECAST = 'weatherForecast',
    WEATHER_LOCATIONS = 'weather',
    WEATHER = 'weather',
    PRODUCTS = 'products',
    PRODUCT_PROGRAM_TYPES = 'productProgramTypes',
    ACCOUNTS = 'accounts',
    METERS = 'meters',
    METER_ENROLLMENTS = 'meterEnrollments',
    METER_ENROLLMENT_ENROLLMENT_IDS = 'meterEnrollmentEnrollmentIds',
    NOMINATIONS = 'nominations',
    NYISO_RESOURCES = 'nyiso-resources',
    NYISO_SEASON = 'nyiso-season',
    INBOX_FILE = 'inbox-file',
    PEAK_EVENT_INDEX = 'peak-event-index',
    PEAK_LOAD = 'peak-load',
    PEAK_DAYS = 'peak-days',
    SC_RESOURCES = 'sc-resources',
    SC_RESOURCE_BIDS = 'sc-resource-bids',
    READINGS_AVAILABILITY_STATS = 'readingsAvailabilityStats',
    RECENTLY_CREATED_OR_CLOSED_SITES_REPORTING = 'recentlyCreatedOrClosedSitesReporting',
    EMAIL_UTILITY_AUTOMATIONS = 'emailUtilityAutomations',
    EMAIL_UTILITY_AUTOMATION_HISTORY = 'emailUtilityAutomationHistory',
    EMAIL_UTILITY_AUTOMATION_HISTORY_CONTENT = 'emailUtilityAutomationHistoryContent',
    SAN_SOURCE_INFO = 'sanSourceInfo',
    EVENT_ANALYTIC_BATCH = 'eventAnalyticBatch',
    SAVED_FILTERS = 'savedFilters',
    EVENT_ANALYTIC_SITE = 'eventAnalyticSite',
    EVENT_ANALYTIC_ENERGY_USAGE = 'eventAnalyticEnergyUsage',
    PJM_LOCATIONS = 'pjmLocations',
    PJM_LOCATION_ZIP_CODES = 'pjmLocationZipCodes',
    PJM_REGISTRATIONS = 'pjmRegistration',
    PJM_REGISTRATION_SEASONS = 'pjmRegistrationSeasons',
    PJM_METER_DATA_UPLOADS = 'pjmMeterDataUploads',
    PJM_SEASON = 'pjmSeason',
    PJM_POLLING_STATUS = 'pjmPollingStatus',
    VENDORS = 'vendors',
    EVENT_ANALYTIC_EARLIEST_EVENT = 'eventAnalyticEarliestEvent',
    NOMINATION_READINESS_TRACKS = 'nominationReadinessTracks',
    RESOURCE_TELEMETRY_STATUS = 'resourceTelemetryStatus',
    API_KEYS = 'apiKeys',
    COMPANY_SETTINGS = 'companySettings',
}
